import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type SaveState = "idle" | "saving" | "saved" | "error";

export interface AutosaveTextareaProps {
  label: string;
  value: string;
  placeholder?: string;
  rows?: number;
  helperText?: string;
  onChange: (value: string) => void;
  onSave: (value: string | null) => Promise<void> | void;
  validate?: (value: string) => string | null;
  allowClear?: boolean;
}

const AutosaveTextarea = ({
  label,
  value,
  placeholder,
  rows = 4,
  helperText,
  onChange,
  onSave,
  validate,
  allowClear = true,
}: AutosaveTextareaProps) => {
  const [status, setStatus] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);

  const save = async (next: string | null) => {
    try {
      setStatus("saving");
      setError(null);
      await onSave(next);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (e: any) {
      setStatus("error");
      setError(String(e?.message || "Save failed"));
    }
  };

  const handleBlur = async () => {
    const trimmed = String(value ?? "").trim();
    if (allowClear && trimmed === "") {
      await save(null);
      return;
    }

    const msg = validate ? validate(value) : null;
    if (msg) {
      setStatus("error");
      setError(msg);
      return;
    }

    await save(value);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        {allowClear && value.trim().length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={async () => {
              onChange("");
              await save(null);
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>
      <Textarea
        rows={rows}
        placeholder={placeholder}
        value={value}
        className="resize-y placeholder:text-muted-foreground/60 placeholder:italic"
        onChange={e => {
          if (error) setError(null);
          onChange(e.target.value);
        }}
        onBlur={handleBlur}
      />
      {helperText ? <div className="text-xs text-muted-foreground mt-1">{helperText}</div> : null}
      <div className={`text-xs mt-1 ${error ? "text-destructive" : "text-muted-foreground"}`}>
        {error ? error : status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "\u00A0"}
      </div>
    </div>
  );
};

export default AutosaveTextarea;
